import type { AlertButton } from '@ionic/vue'
import { alertController } from '@ionic/vue'
import { i18n } from '@/locales/locales'

const { t } = i18n

/**
 * Composable to use the alert window
 * Usage: input the necessary strings and get a Promise<boolean> back
 * INFO: Styling is done in the AppGlobalComponentSetup.vue
 */

export type ConfirmOptions = {
  header: string
  message?: string
  cancelButtonText?: string
  acceptButtonText?: string
  destructive?: boolean
}

export function useConfirm() {
  const confirm = async (options: ConfirmOptions) => {
    const buttons: AlertButton[] = [
      {
        text: options.cancelButtonText || t('words.no'),
        role: 'cancel',
        htmlAttributes: {
          appium: 'cancel-button',
        },
      },
      {
        text: options.acceptButtonText || t('words.yes'),
        role: options.destructive ? 'destructive' : undefined,
        htmlAttributes: {
          appium: 'confirm-button',
        },
      },
    ]

    const alert = await alertController.create({
      header: options.header,
      buttons: buttons,
      message: options.message || t('phrases.are_you_sure'),
      htmlAttributes: {
        appium: 'clockin-confirm',
      },
      mode: 'ios',
    })

    await alert.present()

    const result = await alert.onDidDismiss()

    if (result.role === 'cancel') {
      return false
    }

    if (result.role === 'backdrop') {
      return undefined
    }

    return true
  }

  return {
    confirm,
  }
}
